// src/features/hotels/components/HotelCreate.tsx

import {
  Create,
  SimpleForm,
  TextInput,
  ReferenceInput,
  SelectInput,
  required,
  minLength,
  maxLength,
  useTranslate,
  useNotify,
  useRedirect,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Hotel as HotelIcon } from '@mui/icons-material';
import { MyFormContainer } from '@/components/forms/MyFormContainer';
import { MyFormLayout, FormCard, MyFormSection } from '@/components/forms/MyFormLayout';
import { MyToolbar } from '@/components/forms/MyToolbar';
import { RegistryLinkInput } from '@/components/forms/RegistryLinkInput';

/**
 * Validation rules for hotel creation
 */
const validateHotelName = [
  required('resources.hotels.validation.name_required'),
  minLength(3, 'resources.hotels.validation.name_min_length'),
  maxLength(100, 'resources.hotels.validation.name_max_length'),
];

/**
 * Hotel creation component
 * Only SuperAdmin can access this
 */
export const HotelCreate = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const navigate = useNavigate();

  const handleCancel = () => navigate('/admin-options?tab=hotels');

  const onSuccess = () => {
    notify('resources.hotels.messages.create_success', { type: 'success' });
    redirect('/admin-options?tab=hotels');
  };

  const onError = (error: any) => {
    const errorMessage = error?.body?.error?.message || 
                        error?.message || 
                        translate('resources.hotels.messages.create_error');
    notify(errorMessage, { type: 'error' });
  };

  return (
    <Create
      mutationOptions={{ onSuccess, onError }}
      redirect="list"
      title={translate('resources.hotels.create.title')}
    >
      <SimpleForm toolbar={<MyToolbar resource="hotels" onCancel={handleCancel} />}>
        <MyFormContainer maxWidth="md">
          <MyFormLayout centered maxColumns={1}>
            {/* Basic Information */}
            <FormCard xs={12}>
              <MyFormSection
                title={translate('resources.hotels.create.title')}
                subtitle={translate('resources.hotels.create.subtitle')}
                icon={<HotelIcon />}
                variant="clean"
              >
                {/* Hotel Name */}
                <TextInput
                  source="name"
                  label={translate('resources.hotels.fields.name')}
                  validate={validateHotelName}
                  fullWidth
                  helperText={translate('resources.hotels.validation.name_min_length')}
                />

                {/* Group */}
                <ReferenceInput
                  source="groupId"
                  reference="groups"
                  sort={{ field: 'name', order: 'ASC' }}
                >
                  <SelectInput
                    label={translate('resources.hotels.fields.groupId')}
                    optionText="name"
                    fullWidth
                    helperText={translate('common.labels.optional', 'Optional')}
                  />
                </ReferenceInput>

                {/* Record of the shared registry */}
                <RegistryLinkInput
                  source="directoryId"
                  kind="property"
                  label={translate('resources.hotels.fields.directoryId')}
                />
              </MyFormSection>
            </FormCard>
          </MyFormLayout>
        </MyFormContainer>
      </SimpleForm>
    </Create>
  );
};